export type AccessibleDisplayObject = createjs.DisplayObject & {
  boundsToGlobal?: () => createjs.Rectangle;
  zimTabTag?: HTMLElement;
};

export type AccessibilityFrame = createjs.EventDispatcher & {
  canvas: HTMLCanvasElement;
  stage: createjs.Stage;
  retina: false;
  scale: number;
  shiftKey: boolean;
  shim: false;
  x: number;
  y: number;
};

export type ZimAccessibility<TEntry> = {
  tabIndex: number;
  tabOrder: TEntry[];
  resize(): void;
  dispose(): void;
};

export type FocusHandlers = {
  obj: createjs.DisplayObject;
  onBlur: (event: FocusEvent) => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
};

let canvasId = 0;

/**
 * zimjs imports its own CreateJS fork and assigns it to window.createjs.
 * Preserve the runtime that created the caller's stage and restore it
 * immediately so the caller does not mix objects from two CreateJS
 * namespaces.
 */
export async function loadZimPreservingCreatejs(
  appCreatejs: typeof createjs,
): Promise<typeof import('zimjs')> {
  try {
    return await import('zimjs');
  } finally {
    (
      window as Window & {
        createjs: typeof createjs;
      }
    ).createjs = appCreatejs;
  }
}

export function createAccessibilityFrame(
  stage: createjs.Stage,
  canvasIdPrefix: string,
): {
  frame: AccessibilityFrame;
  dispose: () => void;
  updateGeometry: () => void;
} {
  const canvas = stage.canvas as HTMLCanvasElement;
  if (!canvas.id) canvas.id = `${canvasIdPrefix}-${++canvasId}`;

  const frame = new createjs.EventDispatcher() as AccessibilityFrame;
  frame.canvas = canvas;
  frame.stage = stage;
  frame.retina = false;
  frame.scale = 1;
  frame.shiftKey = false;
  frame.shim = false;
  frame.x = 0;
  frame.y = 0;

  function updateGeometry(): void {
    const bounds = canvas.getBoundingClientRect();
    const parentBounds = canvas.parentElement?.getBoundingClientRect();
    frame.x = parentBounds
      ? bounds.left - parentBounds.left
      : bounds.left + window.scrollX;
    frame.y = parentBounds
      ? bounds.top - parentBounds.top
      : bounds.top + window.scrollY;
    frame.scale = canvas.width ? bounds.width / canvas.width : 1;
  }

  updateGeometry();

  return {
    frame,
    updateGeometry,
    dispose: () => {
      frame.removeAllEventListeners();
    },
  };
}

export function getBoundsToGlobal(
  obj: createjs.DisplayObject,
  bounds: createjs.Rectangle,
): createjs.Rectangle {
  const points = [
    obj.localToGlobal(bounds.x, bounds.y),
    obj.localToGlobal(bounds.x + bounds.width, bounds.y),
    obj.localToGlobal(bounds.x + bounds.width, bounds.y + bounds.height),
    obj.localToGlobal(bounds.x, bounds.y + bounds.height),
  ];
  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const left = Math.min(...xValues);
  const top = Math.min(...yValues);

  return new createjs.Rectangle(
    left,
    top,
    Math.max(...xValues) - left,
    Math.max(...yValues) - top,
  );
}
